<div
  class="branding-component-container brand-panel attribute-list-container scrollable-component-container scrollable-component-settings te-scrollable-container"
  [class.brand-panel--disabled]="!brandingAvailable"
>
  @if (brandingAvailable === null) {
    <div class="brand-panel__plan-message">
      <a tabindex="0" href="#" class="madero-link" uiSref="apps.purchase.home"
        >Subscription Required</a
      >
    </div>
  } @else if (!brandingAvailable) {
    <div class="brand-panel__plan-message">
      <a actionLink class="madero-link" (click)="showUpgradePlanModal()"
        >Plan Upgrade Required</a
      >
    </div>
  }

  <section class="brand-panel__section" aria-labelledby="brand-logo-heading">
    <h3 id="brand-logo-heading" class="brand-panel__section-title">Logo</h3>

    <div
      id="edit-logo"
      class="brand-panel__logo-preview"
      [class.brand-panel__logo-preview--empty]="!hasLogoSet"
      tabindex="0"
      role="button"
      aria-label="Edit logo settings"
      [attr.aria-disabled]="!canEditCompanyBranding"
      (click)="editLogo()"
      (keyup.enter)="editLogo()"
      (keyup.space)="editLogo()"
    >
      @if (logoThumbnailUrl) {
        <img [src]="logoThumbnailUrl" alt="Current brand logo" />
      } @else {
        <span
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-6 tw-text-center"
        >
          <span
            class="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100 tw-text-gray-500"
            aria-hidden="true"
          >
            <mat-icon
              svgIcon="upload-square"
              class="brand-panel__logo-upload-icon"
            ></mat-icon>
          </span>
          <span class="tw-flex tw-flex-col tw-gap-3">
            <span class="brand-panel__logo-title">Add your logo</span>
            <span class="brand-panel__logo-subtitle">
              It can be used across all your presentations.
            </span>
          </span>
        </span>
      }

      <div class="brand-panel__logo-overlay" aria-hidden="true"></div>
      <div class="brand-panel__logo-actions">
        @if (hasLogoSet) {
          <button
            matButton="outlined"
            type="button"
            class="brand-panel__logo-remove"
            aria-label="Remove logo"
            [disabled]="!canEditCompanyBranding"
            (click)="removeLogo($event)"
          >
            <mat-icon svgIcon="close" aria-hidden="true"></mat-icon>
          </button>
        }
        <button
          rv-button
          type="button"
          class="brand-panel__logo-primary-action"
          [disabled]="!canEditCompanyBranding || isLogoUploading"
          (click)="activateLogoUploader($event)"
        >
          Upload a Logo
        </button>
        <button
          rv-button
          variant="secondary"
          type="button"
          class="brand-panel__logo-secondary-action"
          [disabled]="!canEditCompanyBranding || isLogoUploading"
          (click)="selectLogoFromStorage($event)"
        >
          Select from Storage
        </button>
      </div>
    </div>

    <basic-uploader
      uploaderId="brand-logo-uploader"
      [uploadManager]="logoUploadManager"
      validType="image"
      [fileList]="logoUploadList"
      [shown]="isLogoUploading"
    ></basic-uploader>

    <button
      rv-button
      variant="secondary"
      type="button"
      class="brand-panel__action-button"
      [disabled]="!brandingAvailable || !hasLogoSet"
      [attr.aria-disabled]="!brandingAvailable || !hasLogoSet"
      (click)="addLogoToPresentation()"
    >
      Add to presentation
    </button>
  </section>

  <section
    class="brand-panel__card"
    aria-labelledby="brand-colors-heading"
    [class.brand-panel__card--disabled]="!brandingAvailable"
  >
    <div class="brand-panel__card-header">
      <h3 id="brand-colors-heading" class="brand-panel__card-title">
        Brand colors
      </h3>
      <mat-slide-toggle
        id="brand-colors-toggle"
        ariaLabel="Use brand colors"
        [checked]="isBrandColorsEnabled()"
        [disabled]="!canOverrideBranding"
        (change)="toggleBrandColors($event.checked)"
      ></mat-slide-toggle>
    </div>
    <p class="brand-panel__card-description">
      Any changes you make to your branding colors will apply to all
      presentations.
    </p>

    @if (brandColorsExpanded) {
      <div class="brand-panel__inline-editor">
        <h4 class="brand-panel__inline-title">Edit Brand colors</h4>
        <p class="brand-panel__inline-message">
          Any changes you make to your branding colors will apply to all
          presentations in your company.
        </p>

        <div class="brand-panel__color-row">
          <label class="brand-panel__color-label" for="brand-panel-base-color"
            >Dark color</label
          >
          <color-picker
            tabindex="0"
            pickerId="brand-panel-base-color"
            [ngModel]="brandingSettings.baseColor"
            (ngModelChange)="saveBrandColor('baseColor', $event)"
            ngDefaultControl
          ></color-picker>
        </div>

        <div class="brand-panel__color-row">
          <label class="brand-panel__color-label" for="brand-panel-accent-color"
            >Light color</label
          >
          <color-picker
            tabindex="0"
            pickerId="brand-panel-accent-color"
            [ngModel]="brandingSettings.accentColor"
            (ngModelChange)="saveBrandColor('accentColor', $event)"
            ngDefaultControl
          ></color-picker>
        </div>

        <button
          rv-button
          variant="secondary"
          type="button"
          class="brand-panel__action-button"
          (click)="closeBrandColorsEditor()"
        >
          Done
        </button>
      </div>
    } @else {
      <button
        rv-button
        variant="secondary"
        type="button"
        id="edit-colors"
        class="brand-panel__action-button"
        [disabled]="!canEditCompanyBranding"
        [attr.aria-disabled]="!canEditCompanyBranding"
        aria-label="Edit color settings"
        (click)="editColors()"
      >
        Edit branding colors
      </button>
    }
  </section>

  <section
    class="brand-panel__card"
    aria-labelledby="custom-colors-heading"
    [class.brand-panel__card--disabled]="!canOverrideBranding"
  >
    <div class="brand-panel__card-header">
      <h3 id="custom-colors-heading" class="brand-panel__card-title">
        Custom colors
      </h3>
      <mat-slide-toggle
        id="custom-colors-toggle"
        ariaLabel="Use custom colors for this presentation"
        [checked]="isCustomColorsEnabled()"
        [disabled]="!canOverrideBranding"
        (change)="toggleCustomColors($event.checked)"
      ></mat-slide-toggle>
    </div>
    <p class="brand-panel__card-description">
      Turn this on if you want to use your own custom colors in this
      presentation.
    </p>

    @if (isCustomColorsEnabled()) {
      <div class="brand-panel__custom-colors-editor">
        <div class="brand-panel__color-row">
          <label
            class="brand-panel__color-label"
            for="brand-panel-custom-base-color"
            >Dark color</label
          >
          <color-picker
            tabindex="0"
            pickerId="brand-panel-custom-base-color"
            [ngModel]="customColors.baseColor"
            (ngModelChange)="saveCustomColor('baseColor', $event)"
            ngDefaultControl
          ></color-picker>
        </div>

        <div class="brand-panel__color-row">
          <label
            class="brand-panel__color-label"
            for="brand-panel-custom-accent-color"
            >Light color</label
          >
          <color-picker
            tabindex="0"
            pickerId="brand-panel-custom-accent-color"
            [ngModel]="customColors.accentColor"
            (ngModelChange)="saveCustomColor('accentColor', $event)"
            ngDefaultControl
          ></color-picker>
        </div>
      </div>
    }
  </section>
</div>
